<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <div class="oa-page-toolbar">
      <div>
        <h4><i class="fa fa-wrench me-2"></i>Order Amendments Queue</h4>
        <p class="text-muted small mb-0">Triage, review, and safely resolve problematic orders.</p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <router-link
          :to="{ name: 'pay-operations-overview' }"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa fa-map me-1"></i>Overview
        </router-link>
        <router-link
          :to="{ name: 'pay-operations-order-amendments-incidents' }"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa fa-exclamation-circle me-1"></i>Incidents
        </router-link>
        <router-link
          :to="{ name: 'pay-operations-order-amendments-audit' }"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa fa-shield me-1"></i>Audit Log
        </router-link>
        <router-link
          v-if="hasAgentAccess"
          :to="{ name: 'pay-operations-manual-assignment' }"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa fa-plus me-2" />Manual Assignment
        </router-link>
        <router-link
          v-if="hasAgentAccess"
          :to="{ name: 'pay-operations-odoo-payment-update' }"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa fa-credit-card me-1"></i>Odoo Payment Update
        </router-link>
        <router-link
          :to="{ name: 'pay-operations-order-amendments-guide' }"
          class="btn btn-sm btn-outline-primary"
        >
          <i class="fa fa-book me-1"></i>Guide
        </router-link>
      </div>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-3" role="tablist">
      <li v-for="tab in tabs" :key="tab.key" class="nav-item" role="presentation">
        <button
          type="button"
          class="nav-link"
          :class="{ active: activeTab === tab.key }"
          role="tab"
          :aria-selected="activeTab === tab.key"
          @click="switchTab(tab.key)"
        >
          {{ tab.label }}
          <span v-if="tabCounts[tab.key] != null" class="badge bg-secondary ms-1">
            {{ tabCounts[tab.key] }}
          </span>
        </button>
      </li>
    </ul>

    <!-- Filters -->
    <div class="card rfo-card rfo-card--primary oa-search-card">
      <div class="card-body">
        <div class="alert alert-light mb-3" role="alert">
          <i class="fa fa-search font-primary me-2"></i>
          Filter the active workflow, then open a case to review and amend the order.
        </div>
        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label small mb-1">Order UUID</label>
            <input
              v-model="filters.uuid"
              type="text"
              class="form-control border border-primary form-control-sm"
              placeholder="Full or partial UUID..."
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Classification</label>
            <select
              v-model="filters.classification"
              class="form-select border border-primary form-select-sm"
            >
              <option value="">All</option>
              <option value="standard">Standard</option>
              <option value="overshoot">Overshoot</option>
              <option value="ev_reconcile">EV Reconcile</option>
              <option value="drive_away">Drive Away</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Case Status</label>
            <select
              v-model="filters.caseStatus"
              class="form-select border border-primary form-select-sm"
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Assignee</label>
            <div class="input-group input-group-sm">
              <button
                type="button"
                class="form-control border border-primary text-start oa-picker-trigger"
                :title="filters.assignee ? `uid: ${filters.assignee}` : 'Click to pick a ryd user'"
                @click="openAssigneeModal"
              >
                <span v-if="filters.assigneeLabel" class="text-dark">{{
                  filters.assigneeLabel
                }}</span>
                <span v-else-if="filters.assignee" class="font-monospace text-muted small">{{
                  shortUuid(filters.assignee)
                }}</span>
                <span v-else class="text-muted">Click to choose…</span>
              </button>
              <button
                v-if="filters.assignee"
                type="button"
                class="btn btn-outline-secondary"
                aria-label="Clear assignee"
                @click="clearAssignee"
              >
                <i class="fa fa-times" />
              </button>
              <button
                v-else
                type="button"
                class="btn btn-outline-primary"
                aria-label="Pick assignee"
                @click="openAssigneeModal"
              >
                <i class="fa fa-user" />
              </button>
            </div>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Partner</label>
            <input
              v-model="filters.partner"
              type="text"
              class="form-control border border-primary form-control-sm"
              placeholder="Partner name..."
            />
          </div>
          <div class="col-12 oa-action-row">
            <button class="btn btn-outline-secondary" @click="resetFilters">Reset</button>
            <button class="btn btn-outline-primary" @click="copyShareableLink">
              <i class="fa fa-link me-1"></i>Share This Search
            </button>
            <button class="btn btn-success" @click="onSearch">
              <i class="fa fa-search me-1"></i>Search
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card card-absolute rfo-card rfo-table-card rfo-card--secondary oa-results-card">
      <div class="card-header bg-secondary text-white">
        <h5 class="mb-0 text-white">
          <span><i class="fa fa-list me-2"></i>Cases</span>
          <span class="oa-card-header-meta ms-2">{{ totalItems }} total</span>
        </h5>
      </div>
      <div class="card-body border border-secondary rounded-3">
        <!-- Refresh + summary -->
        <div class="oa-card-toolbar">
          <div class="text-muted small">
            <span v-if="totalItems > 0">
              Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalItems }} cases
            </span>
            <span v-else-if="!isLoading">No cases found</span>
          </div>
          <div>
            <button
              v-if="hasAgentAccess"
              class="btn btn-sm btn-outline-secondary"
              :disabled="isRefreshing"
              @click="refreshAllCases"
            >
              <span v-if="isRefreshing">
                <span class="spinner-border spinner-border-sm me-1"></span>Refreshing...
              </span>
              <span v-else> <i class="fa fa-refresh me-1"></i>Refresh from Order Service </span>
            </button>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger outline" role="alert">
          <i class="fa fa-exclamation-triangle me-2"></i>{{ error }}
        </div>

        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-2">Loading cases...</p>
        </div>

        <div v-else-if="cases.length === 0 && !error" class="oa-empty-state">
          <i class="fa fa-check-circle fa-3x text-success mb-3"></i>
          <p class="text-muted">No cases in this tab. All caught up!</p>
        </div>

        <div v-else class="queue-records">
          <div v-if="canBulkSelect" class="queue-select-all">
            <label class="d-inline-flex align-items-center gap-2 mb-0">
              <input
                type="checkbox"
                class="form-check-input border border-primary mt-0"
                :checked="allSelected"
                @change="toggleSelectAll"
              />
              <span>Select all {{ cases.length }} cases on this page</span>
            </label>
          </div>

          <article
            v-for="(c, index) in cases"
            :key="c.orderUuid"
            class="queue-record"
            :class="{ 'queue-record--selected': selectedUuids.includes(c.orderUuid) }"
          >
            <div class="queue-record__header">
              <div class="queue-record__identity">
                <span class="queue-record__position">
                  <span class="visually-hidden">Result</span>
                  {{ (page - 1) * PAGE_SIZE + index + 1 }}
                </span>
                <input
                  v-if="canBulkSelect"
                  type="checkbox"
                  class="form-check-input border border-primary queue-record__checkbox"
                  :checked="selectedUuids.includes(c.orderUuid)"
                  :aria-label="`Select order ${c.orderUuid}`"
                  @change="toggleSelect(c.orderUuid)"
                />
                <div class="queue-record__title-block">
                  <span class="queue-record__label">Order UUID</span>
                  <div class="queue-record__uuid-row">
                    <span class="queue-record__uuid">{{ c.orderUuid }}</span>
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 queue-record__copy"
                      title="Copy UUID"
                      :aria-label="`Copy order UUID ${c.orderUuid}`"
                      @click="copyUuid(c.orderUuid)"
                    >
                      <i class="fa fa-copy" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="queue-record__actions">
                <span class="badge queue-record__badge" :class="stateBadgeClass(c.orderState)">
                  {{ c.orderState }}
                </span>
                <span
                  class="badge queue-record__badge"
                  :class="classificationDisplay(c.classification).badgeClass"
                  :title="c.classification"
                >
                  <i
                    class="fa me-1"
                    :class="classificationDisplay(c.classification).icon"
                    aria-hidden="true"
                  ></i>
                  {{ classificationDisplay(c.classification).label }}
                </span>
                <router-link
                  :to="{
                    name: 'pay-operations-order-amendment-detail',
                    params: { uuid: c.orderUuid },
                  }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="fa fa-folder-open me-1" aria-hidden="true"></i>Open
                </router-link>
              </div>
            </div>

            <div class="queue-record__metrics" aria-label="Order case details">
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">Partner</span>
                <span class="queue-record__metric-value">{{ c.partnerName || '—' }}</span>
              </div>
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">POI</span>
                <span class="queue-record__metric-value font-monospace">{{ c.poiId || '—' }}</span>
              </div>
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">Amount</span>
                <span class="queue-record__metric-value">
                  {{ formatCurrency(c.totalAmount, c.currency) }}
                </span>
              </div>
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">Order Type</span>
                <span class="queue-record__metric-value">{{ c.orderType || '—' }}</span>
              </div>
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">Days Open</span>
                <span class="queue-record__metric-value" :class="slaColorClass(c.daysOpen)">
                  {{ c.daysOpen ?? '—' }}
                </span>
              </div>
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">Assignee</span>
                <span class="queue-record__metric-value">
                  {{ c.assigneeDisplayName || c.assignee || '—' }}
                </span>
              </div>
              <div class="queue-record__metric">
                <span class="queue-record__metric-label">Last Refresh</span>
                <span class="queue-record__metric-value">
                  <ZonedDateTime
                    :value="c.lastRefreshedAt"
                    :zone="userTimezone"
                    kind="user"
                    :with-seconds="false"
                  />
                </span>
              </div>
            </div>
          </article>
        </div>

        <PaginationFleet
          v-if="totalPages > 1"
          class="mt-4"
          :current-page="page"
          :total-pages="totalPages"
          :is-loading="isLoading"
          @page-change="goToPage"
        />

        <div v-if="selectedUuids.length > 0" class="queue-bulk-actions">
          <div>
            <div class="queue-bulk-actions__count">
              <i class="fa fa-check-square-o me-2"></i>
              {{ selectedUuids.length }} order{{ selectedUuids.length === 1 ? '' : 's' }} selected
            </div>
            <p class="queue-bulk-actions__hint">
              Choose an action to review the selected orders before creating the bulk job.
            </p>
          </div>
          <div class="queue-bulk-actions__buttons">
            <button class="btn btn-outline-danger btn-sm" @click="openBulkReview('cancelManually')">
              <i class="fa fa-times-circle me-1"></i>Cancel
            </button>
            <button
              class="btn btn-outline-primary btn-sm"
              @click="openBulkReview('completeManually')"
            >
              <i class="fa fa-check-circle me-1"></i>Complete
            </button>
            <button
              v-if="activeTab === 'ev_reconcile'"
              class="btn btn-outline-warning btn-sm"
              @click="openEvReconcileReview"
            >
              <i class="fa fa-bolt me-1"></i>EV Reconcile Cancel
            </button>
            <button
              v-if="hasAdminAccess"
              class="btn btn-outline-warning btn-sm"
              @click="openBulkReview('forceCompleteManually')"
            >
              <i class="fa fa-shield me-1"></i>Force Complete
            </button>
            <button
              v-if="hasAdminAccess"
              class="btn btn-outline-danger btn-sm"
              @click="openBulkReview('refundAndCancelManually')"
            >
              <i class="fa fa-undo me-1"></i>Refund &amp; Cancel
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="clearSelection">Clear</button>
          </div>
        </div>
      </div>
    </div>

    <BulkActionConfirmModal
      :visible="bulkReviewOpen"
      :action="bulkReviewAction"
      :orders="bulkReviewOrders"
      :is-submitting="isCreatingBulkJob"
      @close="closeBulkReview"
      @submit="executeBulk"
    />

    <BulkProgressModal
      :visible="bulkModalOpen"
      :job-id="bulkJobId"
      :can-retry="hasAdminAccess"
      @close="bulkModalOpen = false"
    />

    <PayOpsUsageInstructions
      class="mt-4"
      description="Triage stuck orders, assign cases, and execute amendments. Cases are classified automatically; pick the tab that matches the workflow you're running."
      :required-roles="queueRequiredRoles"
      :workflow-notes="queueWorkflowNotes"
    />

    <RydUserAutocompleteModal
      :show="assigneeModalOpen"
      title="Select Assignee"
      @select="onAssigneeSelected"
      @close="assigneeModalOpen = false"
    />

    <PayOpsModalShell
      :visible="errorModalOpen"
      title="Operation Error"
      subtitle="An error occurred while processing your request"
      icon="fa-exclamation-triangle"
      tone="danger"
      @close="errorModalOpen = false"
    >
      <div class="alert alert-danger outline mb-0" role="alert">
        <div class="d-flex align-items-start gap-2">
          <i class="fa fa-exclamation-circle fa-lg mt-1"></i>
          <div>
            <h6 class="fw-bold mb-1">Operation Failed</h6>
            <p class="mb-0 text-break">{{ modalErrorMessage }}</p>
          </div>
        </div>
      </div>
      <template #footer>
        <button type="button" class="btn btn-secondary" @click="errorModalOpen = false">
          Close
        </button>
      </template>
    </PayOpsModalShell>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import PaginationFleet from '@/components/common/PaginationFleet.vue';
import BulkActionConfirmModal from '../bulk/BulkActionConfirmModal.vue';
import BulkProgressModal from '../bulk/BulkProgressModal.vue';
import PayOpsModalShell from '../shared/PayOpsModalShell.vue';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import RydUserAutocompleteModal from '../shared/RydUserAutocompleteModal.vue';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';
import {
  copyToClipboard,
  formatCurrency,
  shortIdentifier as shortUuid,
} from '@/utils/display-formatters';
import {
  slaColorClass,
  stateBadgeClass,
  classificationDisplay,
} from '@/services/ryd-pay-operations/order-amendments/formatters';
import { useAmendmentRoles } from '@/services/ryd-pay-operations/order-amendments/use-amendment-roles';
import {
  PAY_OPS_QUEUE_ROLES,
  buildQueueWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';

const router = useRouter();
const route = useRoute();
const toast = useToast();
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);

const { hasAgentAccess, hasAdminAccess } = useAmendmentRoles();

const queueRequiredRoles = PAY_OPS_QUEUE_ROLES;
const queueWorkflowNotes = buildQueueWorkflowNotes();

const PAGE_SIZE = 50;
const tabs = [
  { key: 'all', label: 'All / Active' },
  { key: 'standard', label: 'Standard' },
  { key: 'overshoot', label: 'Overshoot' },
  { key: 'ev_reconcile', label: 'EV Reconcile' },
  { key: 'incidents', label: 'Incidents' },
  { key: 'resolved', label: 'Resolved' },
];

const activeTab = ref(route.query.tab || 'all');
const page = ref(parseInt(route.query.page || '1', 10));
const cases = ref([]);
const totalItems = ref(0);
const tabCounts = ref({});
const isLoading = ref(false);
const isRefreshing = ref(false);
const error = ref('');
const errorModalOpen = ref(false);
const modalErrorMessage = ref('');

function showErrorModal(msg) {
  modalErrorMessage.value = msg || 'An unexpected error occurred.';
  errorModalOpen.value = true;
}

const selectedUuids = ref([]);
const bulkReviewOpen = ref(false);
const bulkReviewAction = ref('');
const bulkReviewUuids = ref([]);
const bulkReviewType = ref('');
const isCreatingBulkJob = ref(false);
const bulkModalOpen = ref(false);
const bulkJobId = ref('');

const filters = reactive({
  uuid: route.query.uuid || '',
  classification: route.query.classification || '',
  caseStatus: route.query.caseStatus || '',
  assignee: route.query.assignee || '',
  assigneeLabel: route.query.assigneeLabel || '',
  partner: route.query.partner || '',
});

const assigneeModalOpen = ref(false);

function openAssigneeModal() {
  assigneeModalOpen.value = true;
}

function onAssigneeSelected(user) {
  filters.assignee = user.uid;
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  filters.assigneeLabel = name || user.email || user.uid;
  assigneeModalOpen.value = false;
  onSearch();
}

function clearAssignee() {
  filters.assignee = '';
  filters.assigneeLabel = '';
  onSearch();
}

const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / PAGE_SIZE)));
const allSelected = computed(
  () =>
    cases.value.length > 0 && cases.value.every((c) => selectedUuids.value.includes(c.orderUuid)),
);
const canBulkSelect = computed(() => activeTab.value !== 'resolved' && hasAgentAccess.value);
const rangeStart = computed(() => (page.value - 1) * PAGE_SIZE + 1);
const rangeEnd = computed(() => Math.min(page.value * PAGE_SIZE, totalItems.value));
const bulkReviewOrders = computed(() =>
  cases.value.filter((item) => bulkReviewUuids.value.includes(item.orderUuid)),
);

function buildQueryParams() {
  const params = { tab: activeTab.value, page: page.value, pageSize: PAGE_SIZE };
  if (filters.uuid) params.search = filters.uuid;
  if (filters.classification) params.classification = filters.classification;
  if (filters.caseStatus) params.caseStatus = filters.caseStatus;
  if (filters.assignee) params.assignee = filters.assignee;
  if (filters.partner) params.partnerName = filters.partner;
  return params;
}

function updateUrl() {
  const query = {};
  if (activeTab.value !== 'all') query.tab = activeTab.value;
  if (page.value > 1) query.page = String(page.value);
  if (filters.uuid) query.uuid = filters.uuid;
  if (filters.classification) query.classification = filters.classification;
  if (filters.caseStatus) query.caseStatus = filters.caseStatus;
  if (filters.assignee) query.assignee = filters.assignee;
  if (filters.assigneeLabel) query.assigneeLabel = filters.assigneeLabel;
  if (filters.partner) query.partner = filters.partner;
  router.replace({ query }).catch(() => {});
}

async function fetchCases() {
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.cases,
      { params: buildQueryParams() },
    );
    cases.value = data?.items ?? [];
    totalItems.value = data?.totalCount ?? 0;
    tabCounts.value = data?.tabCounts ?? {};
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'Failed to load cases.';
    error.value = msg;
    showErrorModal(msg);
  } finally {
    isLoading.value = false;
  }
}

async function refreshAllCases() {
  isRefreshing.value = true;
  try {
    await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.refreshAll,
    );
    toast.success('Case refresh triggered.');
    await fetchCases();
  } catch (err) {
    // The persistent modal is the error surface here (spec §5) — a toast would
    // auto-dismiss the same message before it can be read or copied.
    showErrorModal(err?.response?.data?.message || 'Refresh failed.');
  } finally {
    isRefreshing.value = false;
  }
}

function switchTab(tab) {
  activeTab.value = tab;
  page.value = 1;
  clearSelection();
  updateUrl();
  fetchCases();
}

function goToPage(p) {
  if (p < 1 || p > totalPages.value) return;
  page.value = p;
  updateUrl();
  fetchCases();
}

function onSearch() {
  page.value = 1;
  updateUrl();
  fetchCases();
}

function resetFilters() {
  filters.uuid = '';
  filters.classification = '';
  filters.caseStatus = '';
  filters.assignee = '';
  filters.assigneeLabel = '';
  filters.partner = '';
  page.value = 1;
  updateUrl();
  fetchCases();
}

function copyShareableLink() {
  const baseUrl = window.location.origin + window.location.pathname;
  const params = new URLSearchParams();
  if (activeTab.value !== 'all') params.set('tab', activeTab.value);
  if (filters.uuid) params.set('uuid', filters.uuid);
  if (filters.classification) params.set('classification', filters.classification);
  if (filters.caseStatus) params.set('caseStatus', filters.caseStatus);
  if (filters.assignee) params.set('assignee', filters.assignee);
  if (filters.assigneeLabel) params.set('assigneeLabel', filters.assigneeLabel);
  if (filters.partner) params.set('partner', filters.partner);
  const url = `${baseUrl}?${params.toString()}`;
  navigator.clipboard
    .writeText(url)
    .then(() => toast.success('Shareable link copied!'))
    .catch(() => toast.error('Could not copy link.'));
}

async function copyUuid(uuid) {
  const ok = await copyToClipboard(uuid);
  if (ok) toast.success('UUID copied!');
  else toast.error('Copy failed.');
}

function toggleSelect(uuid) {
  const idx = selectedUuids.value.indexOf(uuid);
  if (idx >= 0) selectedUuids.value.splice(idx, 1);
  else selectedUuids.value.push(uuid);
}

function toggleSelectAll() {
  selectedUuids.value = allSelected.value ? [] : cases.value.map((c) => c.orderUuid);
}

function clearSelection() {
  selectedUuids.value = [];
}

function openBulkReview(action) {
  bulkReviewAction.value = action;
  bulkReviewUuids.value = [...selectedUuids.value];
  // Left blank so the job records the action the modal actually submits — the
  // review may escalate completeManually to forceCompleteManually.
  bulkReviewType.value = '';
  bulkReviewOpen.value = true;
}

function openEvReconcileReview() {
  const evUuids = cases.value
    .filter(
      (item) =>
        item.classification === 'ev_reconcile' && selectedUuids.value.includes(item.orderUuid),
    )
    .map((item) => item.orderUuid);

  if (evUuids.length === 0) {
    toast.info('No EV Reconcile orders selected.');
    return;
  }

  bulkReviewAction.value = 'cancelManually';
  bulkReviewUuids.value = evUuids;
  bulkReviewType.value = 'ev_reconcile';
  bulkReviewOpen.value = true;
}

function closeBulkReview() {
  if (isCreatingBulkJob.value) return;
  bulkReviewOpen.value = false;
}

async function executeBulk({ action, reason, captureOnAmend, orderUuids, confirmations }) {
  // The review modal owns the final selection — the operator may have unticked
  // rows (e.g. already-completed orders) after opening it.
  if (!orderUuids?.length || reason.length < 10) return;
  isCreatingBulkJob.value = true;
  try {
    const { data } = await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.bulkJobs,
      {
        action,
        type: bulkReviewType.value || action,
        reason,
        captureOnAmend,
        orderUuids,
        confirmations,
      },
    );
    bulkJobId.value = data.jobId;
    bulkReviewOpen.value = false;
    bulkModalOpen.value = true;
    clearSelection();
    toast.success('Bulk job created.');
  } catch (err) {
    showErrorModal(err?.response?.data?.message || 'Failed to create bulk job.');
  } finally {
    isCreatingBulkJob.value = false;
  }
}

onMounted(() => {
  fetchCases();
});
</script>

<style scoped>
.queue-records {
  display: grid;
  gap: 1rem;
}

.queue-select-all {
  padding: 0.65rem 0.8rem;
  border: 1px solid #d5dfeb;
  border-radius: 0.45rem;
  background: #f8fafc;
  color: #495057;
  font-size: 0.8rem;
  font-weight: 700;
}

.queue-record {
  position: relative;
  min-width: 0;
  border: 1px solid #d5dfeb;
  border-left: 4px solid #519c8f;
  border-radius: 0.45rem;
  background: #fff;
  box-shadow:
    0 1px 2px rgba(33, 37, 41, 0.05),
    0 8px 18px rgba(33, 37, 41, 0.06);
}

.queue-record--selected {
  border-color: rgba(46, 52, 130, 0.45);
  border-left-color: var(--theme-default, #2e3482);
  box-shadow: 0 0 0 2px rgba(46, 52, 130, 0.08);
}

.queue-record__header {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 0.9rem;
  border-bottom: 1px solid #e9edf3;
  border-top-left-radius: 0.4rem;
  border-top-right-radius: 0.4rem;
  background: #f8fafc;
}

.queue-record__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.7rem;
}

.queue-record__position {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.1rem;
  height: 2.1rem;
  padding: 0 0.4rem;
  flex: 0 0 auto;
  border: 1px solid rgba(81, 156, 143, 0.28);
  border-radius: 0.45rem;
  background: rgba(81, 156, 143, 0.1);
  color: #2f7f74;
  font-size: 0.82rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.queue-record__checkbox {
  flex: 0 0 auto;
  margin: 0;
}

.queue-record__title-block,
.queue-record__uuid-row {
  min-width: 0;
}

.queue-record__label,
.queue-record__metric-label {
  display: block;
  color: #6c757d;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
}

.queue-record__uuid-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.queue-record__uuid {
  min-width: 0;
  color: #212529;
  font-family: var(--bs-font-monospace);
  font-size: 0.92rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.queue-record__copy {
  flex: 0 0 auto;
  color: var(--theme-default, #2e3482);
}

.queue-record__actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
}

.queue-record__badge {
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
}

.queue-record__metrics {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.queue-record__metric {
  display: grid;
  min-width: 0;
  gap: 0.25rem;
  padding: 0.75rem 0.9rem;
  border-right: 1px solid #edf1f6;
  border-bottom: 1px solid #edf1f6;
}

.queue-record__metric:nth-child(4n) {
  border-right: 0;
}

.queue-record__metric:nth-last-child(-n + 3) {
  border-bottom: 0;
}

.queue-record__metric:last-child {
  border-right: 0;
}

.queue-record__metric-value {
  min-width: 0;
  color: #2f3542;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

@media (max-width: 1199.98px) {
  .queue-record__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .queue-record__metric,
  .queue-record__metric:nth-child(4n) {
    border-right: 1px solid #edf1f6;
    border-bottom: 1px solid #edf1f6;
  }

  .queue-record__metric:nth-child(2n),
  .queue-record__metric:last-child {
    border-right: 0;
  }

  .queue-record__metric:last-child {
    border-bottom: 0;
  }
}

@media (max-width: 767.98px) {
  .queue-record__header,
  .queue-record__identity {
    align-items: flex-start;
    flex-direction: column;
  }

  .queue-record__actions {
    justify-content: flex-start;
    width: 100%;
  }

  .queue-record__metrics {
    grid-template-columns: minmax(0, 1fr);
  }

  .queue-record__metric,
  .queue-record__metric:nth-child(2n),
  .queue-record__metric:nth-child(4n) {
    border-right: 0;
    border-bottom: 1px solid #edf1f6;
  }

  .queue-record__metric:last-child {
    border-bottom: 0;
  }
}

.queue-bulk-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(33, 36, 94, 0.2);
  border-left: 4px solid var(--theme-default);
  border-radius: 0.65rem;
  background: #f7f8fc;
}
.queue-bulk-actions__count {
  color: #21245e;
  font-weight: 700;
}
.queue-bulk-actions__hint {
  margin: 0.2rem 0 0;
  color: var(--bs-secondary-color);
  font-size: 0.78rem;
}
.queue-bulk-actions__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}
@media (max-width: 991.98px) {
  .queue-bulk-actions {
    align-items: stretch;
    flex-direction: column;
  }
  .queue-bulk-actions__buttons {
    justify-content: flex-start;
  }
}

.oa-picker-trigger {
  background-color: #fff;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.oa-picker-trigger:hover {
  background-color: rgba(0, 161, 254, 0.04);
}
</style>
